import React, { useCallback, useState } from 'react';
import cx from 'classnames';

import { Button } from '@xarvio/button';
import { IconFont } from '@xarvio/elements';
import { ColorCx, Text } from '@xarvio/corporate-styles';
import { TranslateMessage } from '@xarvio/utils';

import { withAppBasePath } from '@xarvio/frontend-core-platform/lib/basePath';

import { useWhitelabel } from '@xarvio/frontend-core-state/lib/auth/hooks/useWhitelabel/useWhitelabel';
import { PageLayoutAuthBg } from './PageLayoutAuthBg/PageLayoutAuthBg';
import {
  hasLoadedAuthBackground,
  markAuthBackgroundLoaded,
} from './authBackgroundLoadState';
import styles from './PageLayoutAuth.module.scss';

export interface PageLayoutAuthProps {
  title?: string | React.ReactNode;
  onClickClose?: () => void;
  onClickBack?: () => void;
  hasLogo?: boolean;
  noPinBottom?: boolean;
  country?: string;
  className?: string;
  headerBorder?: boolean;
  bgSrc?: string;
  bottomRightEl?: React.ReactNode;
  customLogoEl?: React.ReactNode;
}

export const PageLayoutAuth: React.FC<
  React.PropsWithChildren<PageLayoutAuthProps>
> = ({
  title,
  onClickClose,
  onClickBack,
  hasLogo,
  className,
  headerBorder = true,
  noPinBottom,
  country,
  bgSrc,
  bottomRightEl,
  customLogoEl,
  children,
}) => {
  const { whitelabelConfig } = useWhitelabel();
  const resolvedCustomLogoEl =
    hasLogo && whitelabelConfig?.authLogoElem
      ? whitelabelConfig.authLogoElem
      : customLogoEl;

  const [bgLoaded, setBgLoaded] = useState(() =>
    bgSrc ? hasLoadedAuthBackground(bgSrc) : false,
  );

  const onBgLoad = useCallback(() => {
    if (bgSrc) {
      markAuthBackgroundLoaded(bgSrc);
    }

    window.requestAnimationFrame(() => setBgLoaded(true));
  }, [bgSrc]);

  const bgImgRefCallback = useCallback(
    (node: HTMLImageElement | null) => {
      if (node?.complete && node.naturalWidth > 0) {
        onBgLoad();
      }
    },
    [onBgLoad],
  );

  const classNames = cx(styles.layout, className, {
    [styles.layout_canPinBottom]: !noPinBottom,
    [styles[`layout_country-${country}`]]: country,
  });

  return (
    <div className={classNames}>
      {bgSrc ? (
        <div className={styles.layoutBg}>
          <picture>
            <img
              ref={bgImgRefCallback}
              className={bgLoaded ? styles.layoutBgImgLoaded : undefined}
              src={bgSrc}
              alt=""
              onLoad={onBgLoad}
            />
          </picture>
        </div>
      ) : (
        <PageLayoutAuthBg country={country} />
      )}

      {bottomRightEl && (
        <div className={styles.bottomRightEl}>{bottomRightEl}</div>
      )}

      <div className={styles.panelWrapper}>
        <div className={styles.panel}>
          {(hasLogo || resolvedCustomLogoEl) &&
            (resolvedCustomLogoEl ? (
              <div className={cx(styles.panelLogo, styles.panelLogo_custom)}>
                <div className={styles.logo}>{resolvedCustomLogoEl}</div>
              </div>
            ) : (
              <div className={styles.panelLogo}>
                <div className={styles.logo}>
                  <img
                    src={withAppBasePath('/assets/auth/auth-logo-r.svg')}
                    alt="xarvio"
                    aria-hidden
                  />
                </div>
                <div className={styles.tagline}>
                  <TranslateMessage id={'general.planSmarterGrowBetter'} />
                </div>
              </div>
            ))}

          {(title || onClickBack || onClickClose) && (
            <div
              className={cx(styles.panelHeader, {
                [styles.panelHeader_border]: headerBorder,
              })}
            >
              <div className={styles.back}>
                {onClickBack && (
                  <Button
                    type={'secondary'}
                    size={'small'}
                    iconEl={
                      <IconFont
                        name={'chevron-left'}
                        color={ColorCx.BLACK}
                        size={16}
                      />
                    }
                    label={
                      <Text color={ColorCx.BLACK}>
                        <TranslateMessage id={'general.back'} />
                      </Text>
                    }
                    onClick={onClickBack}
                  />
                )}
              </div>
              <div className={styles.title}>{title}</div>
              <div className={styles.close}>
                {onClickClose && (
                  <Button
                    type={'secondary'}
                    iconEl={
                      <IconFont name={'exit'} color={ColorCx.BLACK} size={16} />
                    }
                    onClick={onClickClose}
                  />
                )}
              </div>
            </div>
          )}

          <div className={styles.panelBody}>{children}</div>
        </div>
      </div>
    </div>
  );
};
